import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchEmergencyStatus, isFeatureLocked } from '@/utils/emergencyLock';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { useNavigate } from 'react-router-dom';

const LeaveApprovalPanel = () => {
  const [requests, setRequests] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [message, setMessage] = useState('');

  const { canApproveLeave, user } = useUserPermissions();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canApproveLeave || !user?.staff_id) {
      navigate('/unauthorized');
    }

    const fetchRequests = async () => {
      try {
        const res = await axios.get('/solo-mdsh-api/staffing/leave-requests');
        setRequests(res.data || []);
      } catch (err) {
        console.error('Failed to load leave requests:', err);
      }
    };

    const checkEmergency = async () => {
      const mode = await fetchEmergencyStatus();
      setIsLocked(isFeatureLocked(mode, ['cyberattack', 'recovery']));
    };

    fetchRequests();
    checkEmergency();
  }, [canApproveLeave, user, navigate]);

  const handleAction = async (leaveId, decision) => {
    try {
      const res = await axios.post(
        `/solo-mdsh-api/staffing/leave-requests/${leaveId}/${decision.toLowerCase()}`,
        { approver_id: user.staff_id }
      );

      setMessage(res.data.message || 'Action completed');
      setRequests((prev) => prev.filter((req) => req.leave_id !== leaveId));
    } catch (err) {
      console.error('Approval error:', err);
      setMessage('❌ Error processing request.');
    }
  };

  return (
    <div className="bg-neutral-800 p-6 rounded-2xl shadow-xl text-white">
      <h2 className="text-xl font-semibold mb-4">🗓️ Leave Approval</h2>

      {isLocked ? (
        <div className="bg-red-600 text-white p-3 rounded-lg font-medium text-center">
          🚫 Leave approval disabled due to Emergency Mode.
        </div>
      ) : requests.length === 0 ? (
        <p className="text-gray-400">No pending leave requests.</p>
      ) : (
        <table className="w-full text-sm table-auto">
          <thead>
            <tr className="text-left border-b border-gray-600">
              <th className="py-2">Staff</th>
              <th>Type</th>
              <th>Date</th>
              <th>Reason</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((req) => (
              <tr key={req.leave_id} className="border-b border-gray-700">
                <td className="py-2">{req.staff_name || '—'}</td>
                <td>{req.leave_type}</td>
                <td>{`${req.start_date} to ${req.end_date}`}</td>
                <td>{req.reason}</td>
                <td>
                  <button
                    className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded mr-2"
                    onClick={() => handleAction(req.leave_id, 'approve')}
                  >
                    Approve
                  </button>
                  <button
                    className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded"
                    onClick={() => handleAction(req.leave_id, 'reject')}
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {message && (
        <div className="mt-4 text-yellow-400 font-medium text-center">{message}</div>
      )}
    </div>
  );
};

export default LeaveApprovalPanel;
